/**
 * "Create an account to scan" — what a guest gets instead of the camera.
 *
 * Reading a photo of a fridge, a receipt or a recipe is a metered server call,
 * and the server only answers it for an account. A guest used to reach the
 * camera anyway, take the photo, and be shown the API's own English "Missing
 * auth token" with a Retry that could only fail again (#775). So every way into
 * a scan asks first, through `useScanGate`, and the screens behind the camera
 * draw `ScanSignUpPanel` if a guest arrives some other way (a deep link, an old
 * tab).
 *
 * Both buttons go to `/auth`, which asks for the email first and decides
 * sign-up or sign-in itself; `next` brings the cook back to the screen they
 * were on (`resolveReturnRoute`).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import { BottomSheet } from '../weekly/v2/primitives/BottomSheet';
import { Paprika, PaprikaLayout } from '../../styles/theme';
import { selectHasAccount } from '../../redux/authSlice';
import { trackEvent } from '../../services/analyticsService';
import { authHrefReturningTo } from '../../utils/postAuthReturn';

/** What the guest was about to photograph. */
export type ScanKind = 'fridge' | 'receipt' | 'recipe';

interface PanelProps {
  kind: ScanKind;
  /** Where the analytics say the guest was stopped. */
  source: string;
  /** The screen to come back to after signing up. */
  returnTo: string;
  /** Closes the sheet, or leaves the screen when the panel is the screen. */
  onLater: () => void;
  /** Runs before navigating to /auth (a sheet closes itself first). */
  onLeave?: () => void;
}

export function ScanSignUpPanel({ kind, source, returnTo, onLater, onLeave }: PanelProps) {
  const { t } = useTranslation();
  const router = useRouter();

  const goToAuth = (action: 'signup' | 'signin') => {
    trackEvent('guest_sign_in_clicked', { source: 'scan_gate', from: source, kind, action });
    onLeave?.();
    router.push(authHrefReturningTo(returnTo) as any);
  };

  return (
    <View style={styles.panel} testID="scan-signup">
      <View style={styles.icon}>
        <Feather name="camera" size={26} color={Paprika.primary} />
      </View>
      <Text style={styles.title}>{t('scanGate.title')}</Text>
      <Text style={styles.body}>{t('scanGate.body')}</Text>

      <TouchableOpacity
        style={styles.primary}
        onPress={() => goToAuth('signup')}
        accessibilityRole="button"
        testID="scan-signup-create"
      >
        <Text style={styles.primaryText}>{t('scanGate.cta')}</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.secondary}
        onPress={() => goToAuth('signin')}
        accessibilityRole="button"
        testID="scan-signup-signin"
      >
        <Text style={styles.secondaryText}>{t('scanGate.signIn')}</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={styles.later}
        onPress={onLater}
        accessibilityRole="button"
        testID="scan-signup-later"
      >
        <Text style={styles.laterText}>{t('scanGate.later')}</Text>
      </TouchableOpacity>
    </View>
  );
}

interface SheetProps extends Omit<PanelProps, 'onLater' | 'onLeave'> {
  visible: boolean;
  onClose: () => void;
}

/**
 * The panel as a bottom sheet on a phone and a centred dialog from 600px, like
 * the Add Recipe sheet it sits beside.
 */
export function ScanSignUpSheet({ visible, onClose, ...panel }: SheetProps) {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const content = <ScanSignUpPanel {...panel} onLater={onClose} onLeave={onClose} />;

  if (width < PaprikaLayout.tabletMinWidth) {
    return (
      <BottomSheet visible={visible} onClose={onClose}>
        {content}
      </BottomSheet>
    );
  }
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.dialogRoot}>
        <Pressable style={styles.scrim} onPress={onClose} accessibilityLabel={t('common.close')} />
        <View style={styles.dialog}>{content}</View>
      </View>
    </Modal>
  );
}

/**
 * Wrap anything that opens a scan. Signed in, `requireAccount` just runs the
 * action; a guest gets the sheet instead, and the moment is counted.
 */
export function useScanGate(source: string, returnTo: string) {
  const hasAccount = useSelector(selectHasAccount);
  const [blockedKind, setBlockedKind] = useState<ScanKind | null>(null);

  const requireAccount = useCallback(
    (kind: ScanKind, action: () => void) => {
      if (hasAccount) {
        action();
        return;
      }
      trackEvent('scan_blocked_guest', { source, kind });
      setBlockedKind(kind);
    },
    [hasAccount, source],
  );

  const sheet = (
    <ScanSignUpSheet
      visible={blockedKind !== null}
      onClose={() => setBlockedKind(null)}
      kind={blockedKind ?? 'fridge'}
      source={source}
      returnTo={returnTo}
    />
  );

  return { hasAccount, requireAccount, sheet };
}

/**
 * The screens behind the camera (camera, scan results, recipe photo) are tabs a
 * guest can still reach by a deep link or an old tab. For a guest they draw the
 * panel instead of mounting the screen, so no camera opens and no request is
 * sent.
 */
export function RequireAccountToScan({
  kind,
  source,
  returnTo,
  children,
}: {
  kind: ScanKind;
  source: string;
  returnTo: string;
  children: React.ReactNode;
}) {
  const hasAccount = useSelector(selectHasAccount);
  const router = useRouter();
  const counted = useRef(false);

  useEffect(() => {
    if (hasAccount || counted.current) return;
    counted.current = true;
    trackEvent('scan_blocked_guest', { source, kind });
  }, [hasAccount, source, kind]);

  if (hasAccount) return <>{children}</>;
  return (
    <View style={styles.screen}>
      <ScanSignUpPanel
        kind={kind}
        source={source}
        returnTo={returnTo}
        onLater={() => router.navigate(returnTo as any)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    justifyContent: 'center',
    backgroundColor: Paprika.background,
    padding: 16,
  },
  panel: {
    alignItems: 'center',
    paddingTop: 16,
    paddingHorizontal: 24,
    paddingBottom: 8,
    width: '100%',
    maxWidth: 420,
    alignSelf: 'center',
  },
  icon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: Paprika.accentLight,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  title: {
    // A "hero moment" — Newsreader carries these, Inter carries the chrome.
    fontFamily: 'Newsreader-Italic',
    fontSize: 24,
    fontStyle: 'italic',
    color: Paprika.textPrimary,
    textAlign: 'center',
    marginBottom: 8,
  },
  body: {
    fontFamily: 'Inter-Regular',
    fontSize: 14,
    lineHeight: 20,
    color: Paprika.textSecondary,
    textAlign: 'center',
    marginBottom: 24,
  },
  primary: {
    backgroundColor: Paprika.primary,
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 24,
    alignSelf: 'stretch',
    alignItems: 'center',
    marginBottom: 8,
  },
  primaryText: {
    fontFamily: 'Inter-SemiBold',
    color: Paprika.onPrimary,
    fontSize: 15,
    fontWeight: '600',
  },
  secondary: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 24,
    alignSelf: 'stretch',
    alignItems: 'center',
    backgroundColor: Paprika.surfaceLow,
    marginBottom: 4,
  },
  secondaryText: {
    fontFamily: 'Inter-Medium',
    color: Paprika.primary,
    fontSize: 14,
    fontWeight: '500',
  },
  later: {
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  laterText: {
    fontFamily: 'Inter-Medium',
    color: Paprika.textSecondary,
    fontSize: 14,
    fontWeight: '500',
  },
  dialogRoot: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  scrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(27, 28, 26, 0.48)',
  },
  dialog: {
    width: '100%',
    maxWidth: 460,
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: 24,
    paddingVertical: 24,
    shadowColor: 'rgba(82, 68, 60, 0.16)',
    shadowOffset: { width: 0, height: 24 },
    shadowOpacity: 1,
    shadowRadius: 48,
    elevation: 24,
  },
});
